import { Checkout } from 'components/checkout'
import { AD_FREE_SUBSCRIPTION_PRICES, AI_SUBSCRIPTION_PRICES, SUBSCRIPTION_PACKAGES } from 'constants/ai'
import { PAGES } from 'constants/routes'
import { WeaknessProfile } from 'constants/stats'
import { useRouter } from 'next/router'
import { useAuthUser } from 'next-firebase-auth'
import React, { useState } from 'react'
import { ActionType, PricingPeriodType } from 'views/pricing/utils'

import {
  UpsellAskMeLaterButton,
  UpsellEmptyStateText,
  UpsellHeaderText,
  UpsellPopupBackdrop,
  UpsellPopupButton,
  UpsellPopupContainer,
  UpsellPopupContent,
  UpsellPopupHeader,
  UpsellPricingCard,
  UpsellPricingFeature,
  UpsellPricingFeatures,
  UpsellPricingFooter,
  UpsellPricingGrid,
  UpsellPricingHeader,
  UpsellPricingPeriod,
  UpsellPricingPrice,
  UpsellPricingTitle,
  UpsellPricingToggle,
  UpsellPricingToggleButton,
  UpsellPricingToggleContainer,
  UpsellPrimaryCTA,
  UpsellSavingsBadge,
  UpsellTaxNote,
  UpsellTrialBadge,
  UpsellWeaknessCategory,
  UpsellWeaknessCategoryLabel,
  UpsellWeaknessChip,
  UpsellWeaknessItems,
  UpsellWeaknessSection,
  UpsellWeaknessTitle,
  UpsellWpmDisplay,
} from './upsell-prompts.styles'

export interface UpsellPromptProps {
  title: string
  description: string
  onClose?: () => void
  showAskMeLater?: boolean
  showOnlyAdFree?: boolean
  showAllowMe2Hours?: boolean
  onAllowMe2Hours?: () => void
  weaknessProfile?: WeaknessProfile | null
  recentWpm?: number
}

/**
 * Upsell prompt component with two-step flow
 * @param title - The title of the upsell prompt
 * @param description - The description of the upsell prompt
 * @param onClose - The function to call when the upsell prompt is closed
 * @param weaknessProfile - User's typing weaknesses (characters, ngrams, words)
 * @param recentWpm - User's recent average WPM
 */
export const UpsellPrompt: React.FC<UpsellPromptProps> = ({
  title = 'Upgrade Your Typing Experience',
  description = 'Get more features and remove ads to focus on improving your typing skills.',
  onClose,
  showAskMeLater = false,
  showOnlyAdFree = false,
  showAllowMe2Hours = false,
  onAllowMe2Hours,
  weaknessProfile,
  recentWpm,
}) => {
  const [pricingPeriod, setPricingPeriod] = useState<PricingPeriodType>(PricingPeriodType.MONTHLY)
  const [showPricing, setShowPricing] = useState(false)
  const authUser = useAuthUser()
  const isLoggedIn = !!authUser.id
  const router = useRouter()

  // Determine if we should show the weakness view (only if we have weakness data)
  const hasWeaknessData = weaknessProfile && recentWpm !== undefined
  const shouldShowWeaknessView = hasWeaknessData && !showPricing

  /**
   * Get the price for the upsell prompt
   * @param planType - The plan type
   * @returns The price
   */
  const getPrice = (planType: 'ad-free' | 'premium') => {
    if (planType === 'ad-free') {
      return pricingPeriod === PricingPeriodType.MONTHLY
        ? AD_FREE_SUBSCRIPTION_PRICES.MONTHLY
        : AD_FREE_SUBSCRIPTION_PRICES.YEARLY
    } else {
      return pricingPeriod === PricingPeriodType.MONTHLY
        ? AI_SUBSCRIPTION_PRICES.MONTHLY
        : AI_SUBSCRIPTION_PRICES.YEARLY
    }
  }

  /**
   * Get the period for the upsell prompt
   * @returns The period
   */
  const getPeriod = () => {
    return pricingPeriod === PricingPeriodType.MONTHLY ? 'month' : 'year'
  }

  /**
   * Get the button text for the upsell prompt
   * @param planType - The plan type
   * @returns The button text
   */
  const getButtonText = (planType: 'ad-free' | 'premium') => {
    // If user is not logged in, show login prompts
    if (!isLoggedIn) {
      return planType === 'ad-free'
        ? 'Login to Remove Ads (7-Day Free Trial)'
        : 'Login to Get AI Coach (7-Day Free Trial)'
    }

    // If user is logged in

    return planType === 'ad-free' ? 'Remove Ads' : 'Get AI Coach'
  }

  /**
   * Get the action type for the upsell prompt
   * @returns The action type
   */
  const getActionType = (): ActionType => {
    return ActionType.SUBSCRIBE
  }

  /**
   * Calculate the savings percentage for the upsell prompt
   * @param planType - The plan type
   * @returns The savings percentage
   */
  const calculateSavingsPercentage = (planType: 'ad-free' | 'premium'): number => {
    const monthly = planType === 'ad-free' ? AD_FREE_SUBSCRIPTION_PRICES.MONTHLY : AI_SUBSCRIPTION_PRICES.MONTHLY
    const yearly = planType === 'ad-free' ? AD_FREE_SUBSCRIPTION_PRICES.YEARLY : AI_SUBSCRIPTION_PRICES.YEARLY

    const yearlyIfMonthly = monthly * 12
    const savings = yearlyIfMonthly - yearly
    const savingsPercentage = (savings / yearlyIfMonthly) * 100
    return parseInt(savingsPercentage.toFixed(0))
  }

  /**
   * Handle positive action
   * This function handles the positive action for the upsell prompt
   * If the user is not logged in, it will redirect to the login page
   * Otherwise, it will call the handle checkout function with the action, pricing period, and plan type to handle the checkout
   * @param action - The action to perform
   * @param pricingPeriod - The pricing period
   * @param planType - The plan type
   * @param handleCheckout - The handle checkout function
   */
  const handlePositiveAction = (
    action: ActionType,
    pricingPeriod: PricingPeriodType,
    planType: (typeof SUBSCRIPTION_PACKAGES)[keyof typeof SUBSCRIPTION_PACKAGES],
    handleCheckout: (metadata: {
      action: ActionType
      pricingPeriod: PricingPeriodType
      planType: (typeof SUBSCRIPTION_PACKAGES)[keyof typeof SUBSCRIPTION_PACKAGES]
    }) => void
  ) => {
    if (!isLoggedIn) {
      router.push(PAGES['/login/'])
    } else {
      handleCheckout({ action, pricingPeriod, planType })
    }
  }

  /**
   * Get contextual weaknesses based on user's WPM level
   * Different skill levels see different weakness categories
   */
  const getContextualWeaknesses = () => {
    if (!weaknessProfile || recentWpm === undefined) return null

    const wpm = recentWpm

    // Beginners (<20 WPM): Show only character weaknesses
    if (wpm < 20) {
      return {
        characters: weaknessProfile.characters.slice(0, 5),
      }
    }

    // Intermediate (20-40 WPM): Show characters + bigrams + common 50 words
    if (wpm < 40) {
      return {
        characters: weaknessProfile.characters.slice(0, 5),
        ngrams: weaknessProfile.ngrams.slice(0, 5),
        words: weaknessProfile.words.slice(0, 5),
      }
    }

    // Advanced (40-60 WPM): Show characters + bi/trigrams + common 100 words
    if (wpm < 60) {
      return {
        characters: weaknessProfile.characters.slice(0, 5),
        ngrams: weaknessProfile.ngrams.slice(0, 5),
        words: weaknessProfile.words.slice(0, 5),
      }
    }

    // Expert (60+ WPM): Show characters + all ngrams + common 150 words
    return {
      characters: weaknessProfile.characters.slice(0, 5),
      ngrams: weaknessProfile.ngrams.slice(0, 5),
      words: weaknessProfile.words.slice(0, 5),
    }
  }

  /**
   * Get personalized premium features based on user's weaknesses
   */
  const getPremiumFeatures = () => {
    const contextualWeaknesses = getContextualWeaknesses()

    // Default features if no weakness data
    if (!contextualWeaknesses) {
      return ['✓ Everything in Ad-Free', '✓ AI-Powered Feedback', '✓ Custom Lessons', '✓ Chat with AI Coach']
    }

    const features: string[] = ['✓ Everything in Ad-Free']

    // Add personalized features based on weaknesses
    if (contextualWeaknesses.characters && contextualWeaknesses.characters.length > 0) {
      const charList = contextualWeaknesses.characters.slice(0, 3).join(', ')
      features.push(`✓ AI targets your weak characters: ${charList}`)
    } else {
      features.push('✓ AI-Powered Feedback')
    }

    features.push('✓ Custom lessons for your problem areas')
    features.push('✓ Real-time feedback on mistakes')
    features.push('✓ Chat with AI Coach')

    return features
  }

  /**
   * Render weakness view showing user's typing weaknesses
   */
  const renderWeaknessView = () => {
    const contextualWeaknesses = getContextualWeaknesses()

    if (!contextualWeaknesses || !recentWpm) {
      return null
    }

    const hasAnyWeaknesses =
      (contextualWeaknesses.characters && contextualWeaknesses.characters.length > 0) ||
      (contextualWeaknesses.ngrams && contextualWeaknesses.ngrams.length > 0) ||
      (contextualWeaknesses.words && contextualWeaknesses.words.length > 0)

    return (
      <>
        <UpsellWeaknessSection>
          <UpsellWpmDisplay>
            <div className="wpm-info">
              <div className="wpm-label">Your Current Speed</div>
              <div className="wpm-display">
                <div className="wpm-value">{recentWpm.toFixed(0)}</div>
                <div className="wpm-unit">WPM</div>
              </div>
            </div>
            <UpsellWeaknessTitle>Your Typing Weaknesses</UpsellWeaknessTitle>
          </UpsellWpmDisplay>

          {hasAnyWeaknesses ? (
            <>
              {contextualWeaknesses.characters && contextualWeaknesses.characters.length > 0 && (
                <UpsellWeaknessCategory>
                  <UpsellWeaknessCategoryLabel>Weak Characters</UpsellWeaknessCategoryLabel>
                  <UpsellWeaknessItems>
                    {contextualWeaknesses.characters.map((char, index) => (
                      <UpsellWeaknessChip key={`char-${index}`}>{char}</UpsellWeaknessChip>
                    ))}
                  </UpsellWeaknessItems>
                </UpsellWeaknessCategory>
              )}

              {contextualWeaknesses.ngrams && contextualWeaknesses.ngrams.length > 0 && (
                <UpsellWeaknessCategory>
                  <UpsellWeaknessCategoryLabel>Weak Letter Combinations</UpsellWeaknessCategoryLabel>
                  <UpsellWeaknessItems>
                    {contextualWeaknesses.ngrams.map((ngram, index) => (
                      <UpsellWeaknessChip key={`ngram-${index}`}>{ngram}</UpsellWeaknessChip>
                    ))}
                  </UpsellWeaknessItems>
                </UpsellWeaknessCategory>
              )}

              {contextualWeaknesses.words && contextualWeaknesses.words.length > 0 && (
                <UpsellWeaknessCategory>
                  <UpsellWeaknessCategoryLabel>Weak Words</UpsellWeaknessCategoryLabel>
                  <UpsellWeaknessItems>
                    {contextualWeaknesses.words.map((word, index) => (
                      <UpsellWeaknessChip key={`word-${index}`}>{word}</UpsellWeaknessChip>
                    ))}
                  </UpsellWeaknessItems>
                </UpsellWeaknessCategory>
              )}
            </>
          ) : (
            <UpsellEmptyStateText>
              <strong>Great job!</strong> You&apos;re typing well. Our AI Coach can help you reach the next level with
              advanced techniques and personalized challenges.
            </UpsellEmptyStateText>
          )}
        </UpsellWeaknessSection>

        <UpsellPrimaryCTA onClick={() => setShowPricing(true)}>Start My Free Trial</UpsellPrimaryCTA>
      </>
    )
  }

  return (
    <>
      <UpsellPopupBackdrop />
      <UpsellPopupContainer>
        <UpsellPopupHeader>
          <UpsellHeaderText>{shouldShowWeaknessView ? 'Your Typing Performance' : title}</UpsellHeaderText>
        </UpsellPopupHeader>
        <UpsellPopupContent>
          {shouldShowWeaknessView ? (
            <>
              <p>
                <strong>Struggling with the same mistakes?</strong> Our AI Coach creates custom lessons targeting YOUR
                exact weaknesses - not generic exercises. Join 50,000+ typists improving their speed.
              </p>
              {renderWeaknessView()}
            </>
          ) : (
            <>
              <p>{description}</p>

              <UpsellPricingToggleContainer>
                <UpsellPricingToggle>
                  <UpsellPricingToggleButton
                    active={pricingPeriod === PricingPeriodType.MONTHLY}
                    onClick={() => setPricingPeriod(PricingPeriodType.MONTHLY)}
                  >
                    {pricingPeriod === PricingPeriodType.MONTHLY && <span style={{ marginRight: '0.5rem' }}>✓</span>}
                    Monthly
                  </UpsellPricingToggleButton>
                  <UpsellPricingToggleButton
                    active={pricingPeriod === PricingPeriodType.YEARLY}
                    onClick={() => setPricingPeriod(PricingPeriodType.YEARLY)}
                  >
                    {pricingPeriod === PricingPeriodType.YEARLY && <span style={{ marginRight: '0.5rem' }}>✓</span>}
                    Yearly
                  </UpsellPricingToggleButton>
                </UpsellPricingToggle>
              </UpsellPricingToggleContainer>

              <UpsellPricingGrid showOnlyAdFree={showOnlyAdFree}>
                <UpsellPricingCard>
                  {pricingPeriod === PricingPeriodType.YEARLY && (
                    <UpsellSavingsBadge>Save {calculateSavingsPercentage('ad-free')}%</UpsellSavingsBadge>
                  )}
                  <UpsellPricingHeader>
                    <UpsellPricingTitle>
                      {pricingPeriod === PricingPeriodType.MONTHLY ? 'Monthly Ad-Free' : 'Yearly Ad-Free'}
                    </UpsellPricingTitle>

                    <UpsellTrialBadge>🎁 7-Day Free Trial</UpsellTrialBadge>
                    <UpsellPricingPrice>
                      ${getPrice('ad-free')}
                      <UpsellPricingPeriod>/{getPeriod()}</UpsellPricingPeriod>
                    </UpsellPricingPrice>
                    <UpsellTaxNote>Prices exclude taxes (added at checkout)</UpsellTaxNote>
                  </UpsellPricingHeader>
                  <UpsellPricingFeatures>
                    <UpsellPricingFeature>✓ Ad-free experience</UpsellPricingFeature>
                    <UpsellPricingFeature>✓ Priority Support</UpsellPricingFeature>
                  </UpsellPricingFeatures>
                  <UpsellPricingFooter>
                    <Checkout>
                      {(handleCheckout) => (
                        <UpsellPopupButton
                          onClick={() =>
                            handlePositiveAction(
                              getActionType(),
                              pricingPeriod,
                              SUBSCRIPTION_PACKAGES.AD_FREE,
                              handleCheckout
                            )
                          }
                        >
                          {getButtonText('ad-free')}
                        </UpsellPopupButton>
                      )}
                    </Checkout>
                  </UpsellPricingFooter>
                </UpsellPricingCard>
                {!showOnlyAdFree && (
                  <UpsellPricingCard>
                    {pricingPeriod === PricingPeriodType.YEARLY && (
                      <UpsellSavingsBadge>Save {calculateSavingsPercentage('premium')}%</UpsellSavingsBadge>
                    )}
                    <UpsellPricingHeader>
                      <UpsellPricingTitle>
                        {pricingPeriod === PricingPeriodType.MONTHLY ? 'Monthly Premium' : 'Yearly Premium'}
                      </UpsellPricingTitle>
                      <UpsellTrialBadge>🎁 7-Day Free Trial</UpsellTrialBadge>
                      <UpsellPricingPrice>
                        ${getPrice('premium')}
                        <UpsellPricingPeriod>/{getPeriod()}</UpsellPricingPeriod>
                      </UpsellPricingPrice>
                      <UpsellTaxNote>Prices exclude taxes (added at checkout)</UpsellTaxNote>
                    </UpsellPricingHeader>
                    <UpsellPricingFeatures>
                      {getPremiumFeatures().map((feature, index) => (
                        <UpsellPricingFeature key={`premium-feature-${index}`}>{feature}</UpsellPricingFeature>
                      ))}
                    </UpsellPricingFeatures>
                    <UpsellPricingFooter>
                      <Checkout>
                        {(handleCheckout) => (
                          <UpsellPopupButton
                            onClick={() =>
                              handlePositiveAction(
                                getActionType(),
                                pricingPeriod,
                                SUBSCRIPTION_PACKAGES.PREMIUM,
                                handleCheckout
                              )
                            }
                          >
                            {getButtonText('premium')}
                          </UpsellPopupButton>
                        )}
                      </Checkout>
                    </UpsellPricingFooter>
                  </UpsellPricingCard>
                )}
              </UpsellPricingGrid>
            </>
          )}

          {showAskMeLater && <UpsellAskMeLaterButton onClick={onClose}>Ask me later</UpsellAskMeLaterButton>}
          {showAllowMe2Hours && (
            <UpsellAskMeLaterButton onClick={onAllowMe2Hours}>Allow me 2 hours</UpsellAskMeLaterButton>
          )}
        </UpsellPopupContent>
      </UpsellPopupContainer>
    </>
  )
}
